import { SectionHeader } from "@/components/common/section-header";
import type { Excursion } from "@/types/dto/pdp/pdp-itinerary";
import { Box, Collapse, Dialog, Grid } from "@mui/material";
import React, { useState } from "react";
import { useTranslations } from "next-intl";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import { ExcursionCard } from "../itinerary-section/components";
import { ExcursionModal } from "../itinerary-section/components/upgrade-modals";
import ExperienceAddOnSkeleton from "./experience-add-on-skeleton";

interface ExperienceAddOnProps {
  dayUpgrades: Excursion[];
  isLoading: boolean;
}

const ExperienceAddOn = ({ dayUpgrades, isLoading }: ExperienceAddOnProps) => {
  const t = useTranslations("PDP");
  const [selectedUpgrade, setSelectedUpgrade] = useState<Excursion | null>(null);
  const [showAll, setShowAll] = useState(false);

  if (isLoading) return <ExperienceAddOnSkeleton />;
  if (!dayUpgrades || dayUpgrades.length === 0) return null;

  const INITIAL_VISIBLE_COUNT = 4;
  const hasMoreThanFour = dayUpgrades.length > INITIAL_VISIBLE_COUNT;
  const initialUpgrades = dayUpgrades.slice(0, INITIAL_VISIBLE_COUNT);
  const remainingUpgrades = dayUpgrades.slice(INITIAL_VISIBLE_COUNT);

  const handleOpen = (excursion: Excursion) => {
    setSelectedUpgrade(excursion);
  };

  const handleClose = () => {
    setSelectedUpgrade(null);
  };

  const handleToggleShowAll = () => {
    setShowAll((prev) => !prev);
  };

  return (
    <Box
      className="fluid-box"
      component="section"
      sx={{ backgroundColor: "background.cool_strong" }}
      data-track="visibility"
      data-parent-container-type="Optional Add-On Experiences"
      data-module-name="optional-add-on-experiences-section"
      data-module-ranking-position="13"
      id="optional-experiences"
    >
      <Box
        className="full-width-container"
        sx={{
          padding: { xs: "4rem 2.4rem", lg: "4rem 6.5rem" },
          display: "flex",
          flexDirection: "column",
          gap: "4rem"
        }}
      >
        <SectionHeader
          headerText={t("Optional Experiences")}
          headerTestId="optional-experiences-header"
          copyTestId="optional-experiences-copy"
          headerSx={{ textTransform: "none", textAlign: "center" }}
          copyText={t("Optional Experiences Description")}
          copySx={{ textAlign: "center" }}
          sx={{ gap: { xs: "1.2rem" }, color: "primary.main" }}
        />
        <Grid container rowSpacing="4rem" columnSpacing="2.4rem">
          {initialUpgrades.map((upgrade, index) => (
            <Grid
              size={{ xs: 12, lg: 6 }}
              key={upgrade.title}
              data-testid={`experience-add-on_upgrade-card-grid_${index}`}
            >
              <ExcursionCard
                excursion={upgrade}
                onClick={handleOpen}
                testIdPrefix={`${index}`}
                showChip={false}
                wideCard
              />
            </Grid>
          ))}
          <Collapse in={showAll} timeout="auto">
            <Grid container rowSpacing="4rem" columnSpacing="2.4rem" sx={{ mt: 0 }}>
              {remainingUpgrades.map((upgrade, index) => (
                <Grid
                  size={{ xs: 12, lg: 6 }}
                  key={upgrade.title}
                  data-testid={`experience-add-on_upgrade-card-grid_${INITIAL_VISIBLE_COUNT + index}`}
                >
                  <ExcursionCard
                    excursion={upgrade}
                    onClick={handleOpen}
                    testIdPrefix={`${INITIAL_VISIBLE_COUNT + index}`}
                    showChip={false}
                    wideCard
                  />
                </Grid>
              ))}
            </Grid>
          </Collapse>
        </Grid>

        {hasMoreThanFour && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ColletteButton
              label={showAll ? "Show Less" : "Show All Options"}
              onClick={handleToggleShowAll}
              size="large"
              testId="experience-add-on_toggle-button"
              variant="outlined"
              sx={{
                backgroundColor: "background.default"
              }}
            />
          </Box>
        )}
      </Box>

      {selectedUpgrade && (
        <Dialog
          open
          onClose={handleClose}
          maxWidth="md"
          fullWidth
          disableScrollLock
          data-testid="experience-add-on_excursion-modal"
        >
          <ExcursionModal excursion={selectedUpgrade} handleClose={handleClose} />
        </Dialog>
      )}
    </Box>
  );
};
export default ExperienceAddOn;
